import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import type { AssetCard } from '@/lib/api/types';
import { IconButton } from '@/components/Button';
import { useFocusTrap } from '@/lib/useFocusTrap';
import { formatBytes, formatDateTime } from '@/lib/format';
import { msToClock } from '@/lib/media/timecode';

/** Space-bar large preview overlay, no route change (§2.10). */
export function QuickLook({ card, onClose, onOpen }: { card: AssetCard; onClose: () => void; onOpen: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useFocusTrap(ref, { active: true, initialFocus: 'container' }); // Space/Esc/Enter handled below (capture)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === ' ' || e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        onOpen();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose, onOpen]);
  const src = card.thumbnails['1024'] ?? card.thumbnails['512'] ?? card.thumbnails['256'] ?? undefined;
  return createPortal(
    <div ref={ref} tabIndex={-1} className="fixed inset-0 z-[90] flex items-center justify-center bg-black/80 p-6 outline-none" role="dialog" aria-modal="true" aria-label={`Quick look: ${card.title}`} onClick={onClose}>
      <div className="absolute top-3 right-3">
        <IconButton label="Close" onClick={onClose} className="text-white hover:bg-white/10">
          <X size={18} />
        </IconButton>
      </div>
      <figure className="flex max-h-full max-w-full flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
        {card.type === 'video' && card.preview_mp4_url ? (
          <video src={card.preview_mp4_url} poster={src} controls autoPlay muted playsInline className="max-h-[80vh] max-w-[90vw] rounded-md" />
        ) : (
          <img src={src} alt={card.title} className="max-h-[80vh] max-w-[90vw] rounded-md object-contain" style={{ background: card.dominant_colors?.[0]?.hex }} />
        )}
        <figcaption className="text-center text-sm text-white/90">
          <div className="font-medium">{card.title}</div>
          <div className="text-xs text-white/60">
            {card.width && card.height ? `${card.width}×${card.height} · ` : ''}
            {card.duration_ms ? `${msToClock(card.duration_ms)} · ` : ''}
            {formatBytes(card.size_bytes)} · {formatDateTime(card.created_at)}
          </div>
        </figcaption>
      </figure>
    </div>,
    document.body,
  );
}
